/**
 * Ticket KL3: Verwaltung der Herkünfte. Reiter neben Anfragen und Kontaktliste.
 *
 * Pflege durch Administratorinnen und Editorinnen; das endgültige Entfernen ist
 * Administratorinnen vorbehalten und wird serverseitig geprüft. Die vom System
 * vergebenen Herkünfte bleiben pflegbar, sind aber gegen Löschen geschützt.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Lock, Trash2 } from "lucide-react";
import {
  createContactSource,
  listContactSources,
  removeContactSource,
  updateContactSource,
} from "@/lib/contact-sources.functions";
import {
  CONTACT_SOURCE_DESCRIPTION_MAX,
  CONTACT_SOURCE_NAME_MAX,
  CONTACT_SOURCE_REMOVAL_HINTS,
  CONTACT_SOURCE_REMOVAL_LABELS,
  isProtectedSource,
  type ContactSource,
  type ContactSourceRemovalMode,
} from "@/lib/contact-sources";
import { useAdminSession } from "@/hooks/use-admin-session";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminTableShell } from "@/components/admin/AdminTableShell";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_admin/admin/contact-sources")({
  component: ContactSourcesPage,
  head: () => ({ meta: [{ title: "Herkünfte – Interner Bereich" }] }),
});

const selectClass = adminInputClass + " pr-8";

function ContactSourcesPage() {
  const { isAdmin } = useAdminSession();
  const queryClient = useQueryClient();

  const listFn = useServerFn(listContactSources);
  const q = useQuery({
    queryKey: ["admin", "contact-sources"],
    queryFn: () => listFn(),
    staleTime: 60_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "contact-sources"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ContactSource | null>(null);
  const [removing, setRemoving] = useState<ContactSource | null>(null);

  const rows = q.data ?? [];

  return (
    <div>
      <AdminPageHeader
        title="Herkünfte"
        description="Geführte Liste der Herkünfte. Am Kontakt wird ausschließlich aus den aktiven Herkünften gewählt."
        actions={
          <Button type="button" onClick={() => setCreateOpen(true)}>
            Herkunft anlegen
          </Button>
        }
      />

      <AdminCard>
        {q.isLoading ? (
          <AdminSkeletonList rows={5} />
        ) : q.isError ? (
          <AdminErrorNotice
            message={q.error instanceof Error ? q.error.message : "Herkünfte nicht lesbar."}
            action={
              <Button type="button" variant="outline" onClick={() => void q.refetch()}>
                Erneut versuchen
              </Button>
            }
          />
        ) : (
          <AdminTableShell>
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-3 py-2">Sortierung</th>
                  <th className="px-3 py-2">Bezeichnung</th>
                  <th className="px-3 py-2">Beschreibung</th>
                  <th className="px-3 py-2">Kontakte</th>
                  <th className="px-3 py-2">Zustand</th>
                  <th className="px-3 py-2 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{row.sortOrder}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        className="font-display text-sm font-extrabold text-brand-petrol underline-offset-2 hover:underline"
                      >
                        {row.name}
                      </button>
                      {isProtectedSource(row) ? (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground"
                          title="Wird vom System vergeben und kann nicht endgültig entfernt werden."
                        >
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          vom System vergeben
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {row.description ?? "–"}
                    </td>
                    <td className="px-3 py-2 text-xs">{row.usageCount}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.isActive ? (
                        <span className="rounded-md bg-brand-petrol/10 px-2 py-1 font-display text-xs font-extrabold text-brand-petrol">
                          aktiv
                        </span>
                      ) : (
                        <span className="rounded-md bg-muted px-2 py-1 font-display text-xs font-extrabold text-foreground">
                          deaktiviert
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setRemoving(row)}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Entfernen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableShell>
        )}
      </AdminCard>

      <SourceFormDialog
        open={createOpen || editing !== null}
        source={editing}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreateOpen(false);
          setEditing(null);
          refresh();
        }}
      />

      <RemoveDialog
        source={removing}
        others={rows.filter((r) => r.id !== removing?.id && r.isActive)}
        isAdmin={isAdmin}
        onClose={() => setRemoving(null)}
        onDone={() => {
          setRemoving(null);
          refresh();
        }}
      />
    </div>
  );
}

function SourceFormDialog({
  open,
  source,
  onClose,
  onSaved,
}: {
  open: boolean;
  source: ContactSource | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const key = source?.id ?? "neu";
  const [form, setForm] = useState({
    name: source?.name ?? "",
    description: source?.description ?? "",
    isActive: source?.isActive ?? true,
    sortOrder: String(source?.sortOrder ?? 100),
  });
  // Wechselt der Datensatz, wird das Formular neu befüllt.
  const [loaded, setLoaded] = useState(key);
  if (loaded !== key) {
    setLoaded(key);
    setForm({
      name: source?.name ?? "",
      description: source?.description ?? "",
      isActive: source?.isActive ?? true,
      sortOrder: String(source?.sortOrder ?? 100),
    });
  }

  const createFn = useServerFn(createContactSource);
  const updateFn = useServerFn(updateContactSource);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        isActive: form.isActive,
        sortOrder: Number.parseInt(form.sortOrder, 10) || 100,
      };
      if (source) return updateFn({ data: { id: source.id, ...payload } });
      return createFn({ data: payload });
    },
    onSuccess: () => {
      toast.success(source ? "Herkunft gespeichert." : "Herkunft angelegt.");
      onSaved();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Speichern nicht möglich."),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{source ? "Herkunft bearbeiten" : "Herkunft anlegen"}</DialogTitle>
          <DialogDescription>
            {source && isProtectedSource(source)
              ? "Diese Herkunft wird vom System vergeben. Bezeichnung, Beschreibung und Sortierung sind pflegbar."
              : "Am Kontakt erscheinen nur aktive Herkünfte zur Auswahl."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <AdminField label="Bezeichnung" required>
            {(p) => (
              <input
                {...p}
                className={adminInputClass}
                maxLength={CONTACT_SOURCE_NAME_MAX}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            )}
          </AdminField>
          <AdminField
            label="Beschreibung"
            hint={`Höchstens ${CONTACT_SOURCE_DESCRIPTION_MAX} Zeichen (${form.description.length}).`}
          >
            {(p) => (
              <textarea
                {...p}
                rows={3}
                className={adminInputClass}
                maxLength={CONTACT_SOURCE_DESCRIPTION_MAX}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            )}
          </AdminField>
          <div className="grid gap-3 sm:grid-cols-2">
            <AdminField label="Sortierung" hint="Kleinere Zahl steht oben.">
              {(p) => (
                <input
                  {...p}
                  type="number"
                  min={0}
                  max={9999}
                  className={adminInputClass}
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                />
              )}
            </AdminField>
            <AdminField label="Zustand">
              {(p) => (
                <select
                  {...p}
                  className={selectClass}
                  value={form.isActive ? "aktiv" : "deaktiviert"}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === "aktiv" }))}
                >
                  <option value="aktiv">aktiv</option>
                  <option value="deaktiviert">deaktiviert</option>
                </select>
              )}
            </AdminField>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={mutation.isPending || form.name.trim().length === 0}
            onClick={() => mutation.mutate()}
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Punkt 4 des Tickets: drei Wege beim Entfernen einer Herkunft. */
function RemoveDialog({
  source,
  others,
  isAdmin,
  onClose,
  onDone,
}: {
  source: ContactSource | null;
  others: ContactSource[];
  isAdmin: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<ContactSourceRemovalMode>("deactivate");
  const [targetId, setTargetId] = useState("");

  const removeFn = useServerFn(removeContactSource);
  const mutation = useMutation({
    mutationFn: () =>
      removeFn({
        data: {
          id: source!.id,
          mode,
          targetId: mode === "transfer" ? targetId : null,
        },
      }),
    onSuccess: (result) => {
      const affected = (result as { affected?: number }).affected ?? 0;
      toast.success(
        mode === "deactivate"
          ? `Herkunft deaktiviert. ${affected} Kontakte behalten den Vermerk.`
          : mode === "transfer"
            ? `${affected} Kontakte wurden übertragen.`
            : `Herkunft entfernt. ${affected} Vermerke wurden gelöscht.`,
      );
      setMode("deactivate");
      setTargetId("");
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Entfernen nicht möglich."),
  });

  const protected_ = source ? isProtectedSource(source) : false;
  const modes = useMemo<ContactSourceRemovalMode[]>(
    () => ["deactivate", "transfer", "delete"],
    [],
  );

  return (
    <Dialog open={source !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Herkunft entfernen</DialogTitle>
          <DialogDescription>
            {source
              ? `„${source.name}" ist bei ${source.usageCount} Kontakten vermerkt. Bitte wählen Sie, was mit diesen Kontakten geschehen soll.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {modes.map((m) => {
            const blockedDelete = m === "delete" && (!isAdmin || protected_);
            const blockedTransfer = m === "transfer" && others.length === 0;
            const blocked = blockedDelete || blockedTransfer;
            return (
              <label
                key={m}
                className={
                  "flex gap-3 rounded-md border p-3 text-sm " +
                  (blocked ? "border-border opacity-50" : "border-border")
                }
              >
                <input
                  type="radio"
                  name="removal-mode"
                  className="mt-1"
                  checked={mode === m}
                  disabled={blocked}
                  onChange={() => setMode(m)}
                />
                <span>
                  <span className="font-display font-extrabold">
                    {CONTACT_SOURCE_REMOVAL_LABELS[m]}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {CONTACT_SOURCE_REMOVAL_HINTS[m]}
                  </span>
                  {blockedDelete ? (
                    <span className="block text-xs text-destructive">
                      {protected_
                        ? "Diese Herkunft wird vom System vergeben und bleibt erhalten."
                        : "Nur Administratoren."}
                    </span>
                  ) : null}
                  {blockedTransfer ? (
                    <span className="block text-xs text-destructive">
                      Es gibt keine andere aktive Herkunft als Ziel.
                    </span>
                  ) : null}
                  {m === "transfer" && mode === "transfer" && others.length > 0 ? (
                    <select
                      aria-label="Ziel-Herkunft"
                      className={selectClass + " mt-2"}
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                    >
                      <option value="">Ziel wählen …</option>
                      {others.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={mutation.isPending || (mode === "transfer" && targetId.length === 0)}
            onClick={() => mutation.mutate()}
          >
            {CONTACT_SOURCE_REMOVAL_LABELS[mode]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
